// based imports and stores
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth.js';

// imports axios
import axios from 'axios';

class ApiService {

  constructor(baseURL) {

    this.api = axios.create({
      baseURL: baseURL ?? "https://fantablondin-ni0l.onrender.com",
      timeout: 25000
    });

    // Interceptor per aggiungere il token JWT
    this.api.interceptors.request.use(config => {
      const token = computed(() => useAuthStore().getToken)?.value;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    }, error => {
      return Promise.reject(error);
    });

  }

  handleResponse(response) {
    if (response?.status !== 200) {
      throw new Error('La chiamata API non è riuscita o non ha successo');
    }

    if (!('data' in response) || response?.data === null) {
      throw new Error('La chiamata API non contiene dati');
    }

    // Controlla se il responseType è "blob"
    if (response.config.responseType === 'blob') {
      return response.data;
    }

    if (!('res' in response?.data) || response?.data?.res !== true) {
      throw new Error('La chiamata API ha restituito esito non valido');
    }

    return response?.data;
  }

  /*
    Estraggo il messaggio del backend solo quando c'e' davvero: con
    responseType 'blob' o con una risposta HTML (es. 404 di express) `data`
    non e' un oggetto JSON e concatenarlo produceva "... : undefined".
  */
  #extractMessage(error) {
    const data = error?.response?.data;

    if (!data || typeof data !== 'object' || (typeof Blob !== 'undefined' && data instanceof Blob)) {
      return null;
    }

    return typeof data.message === 'string' && data.message.trim() !== '' ? data.message.trim() : null;
  }

  handleError(error) {
    if (error.response) {

      // prefissi per stato, allineati ai messaggi storici
      const prefixes = {
        400: 'Richiesta non valida',
        401: 'Non autorizzato',
        403: 'Accesso negato',
        404: 'Risorsa non trovata',
        410: 'Servizio non piu\' disponibile',
        422: 'Errore di validazione',
        429: 'Troppi tentativi di accesso',
        500: 'Errore interno del server',
        502: 'Errore di rete',
        503: 'Servizio non disponibile',
        504: 'Timeout'
      };

      const prefix = prefixes[error.response.status] || 'Errore';
      const message = this.#extractMessage(error);

      return new Error(message ? `${prefix}: ${message}` : prefix);

    } else if (error.request) {
      return new Error('Errore di rete: Impossibile raggiungere il server.');
    } else {
      return new Error('Errore nella richiesta: ' + error.message);
    }
  }

  async request(method, endpoint, data = null, config = {}) {
    try {
      const response = await this.api.request({ method, url: endpoint, data, ...config });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async get(endpoint) {
    return this.request('get', endpoint);
  }

  async post(endpoint, data, responseType = null) {
    return this.request('post', endpoint, data, responseType ? { responseType } : {});
  }

  async put(endpoint, data) {
    return this.request('put', endpoint, data);
  }

  async delete(endpoint) {
    return this.request('delete', endpoint);
  }

  /*
    Upload multipart. Accetta config extra (onUploadProgress, signal, timeout):
    il timeout di default della classe (25s) e' troppo corto per un lotto di file.
  */
  async postFile(endpoint, data, config = {}) {
    return this.request('post', endpoint, data, {
      ...config,
      headers: { 'Content-Type': 'multipart/form-data', ...(config.headers || {}) }
    });
  }

  setBaseURL(newBaseURL) {
    this.api.defaults.baseURL = newBaseURL;
  }

  setHeaders(headers) {
    this.api.defaults.headers = { ...this.api.defaults.headers, ...headers };
  }
  
}

const HttpService = new ApiService(import.meta.env.VITE_API_URL);

export default HttpService;
